import React from 'react';
import Card from 'src/components/Card/Card';
import CalloutCard from 'src/components/CalloutCard/CalloutCard';
import ActivationLinkField from './ActivationLinkField';
import { useActivationLinkGenerator } from './useActivationLinkGenerator';
import './ActivationLinkCard.scss';

interface ActivationLinkCardProps {
  userId: string;
  status: string;
  viewerRole: string;
}

const PLACEHOLDER_LINK: string =
  'https://app.juniper.com/activate/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';

const ALLOWED_VIEWER_ROLES: ReadonlySet<string> = new Set<string>([
  'Admin',
  'Super Admin',
]);

const GENERATE_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set<string>([
  'Created',
  'Invited',
]);

const ALREADY_ACTIVATED_NOTE: string =
  'This contact has already activated their account — no activation link is needed.';

const INVALIDATION_NOTE: string =
  "Generating a new link immediately invalidates any activation link previously sent to this contact, even if they haven't used it yet. Copy and send this one before you leave, it won't be shown again.";

const formatExpiry = (iso: string): string => {
  const date: Date = new Date(iso);

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ActivationLinkCard = ({
  userId,
  status,
  viewerRole,
}: ActivationLinkCardProps) => {
  const {
    activationLink,
    expiresOn,
    isGenerating,
    isRevealed,
    error,
    generate,
    copyLink,
  } = useActivationLinkGenerator(userId);

  const canManage: boolean = ALLOWED_VIEWER_ROLES.has(viewerRole);
  const isAlreadyActivated: boolean = status === 'Active';
  const canGenerate: boolean = GENERATE_ELIGIBLE_STATUSES.has(status);

  if (!canManage) {
    return null;
  }

  if (isAlreadyActivated) {
    return (
      <Card
        title="Activation link"
        body={
          <CalloutCard
            icon_type="info"
            size="small"
            message={ALREADY_ACTIVATED_NOTE}
          />
        }
      />
    );
  }

  if (!canGenerate) {
    return null;
  }

  const buttonLabel: string = isRevealed ? 'Generate new link' : 'Fetch link';
  const linkText: string = activationLink ?? PLACEHOLDER_LINK;

  const expiryLine: React.ReactNode =
    isRevealed && expiresOn ? (
      <p className="expiry">Expires {formatExpiry(expiresOn)}</p>
    ) : null;

  const errorLine: React.ReactNode = error ? (
    <p className="error-text">{error}</p>
  ) : null;

  const body = (
    <div className="activation-link-card">
      <ActivationLinkField
        link={linkText}
        isRevealed={isRevealed}
        onCopy={copyLink}
        actionLabel={buttonLabel}
        isGenerating={isGenerating}
        onGenerate={generate}
      />
      {expiryLine}
      <CalloutCard
        icon_type="warning"
        size="small"
        message={INVALIDATION_NOTE}
      />
      {errorLine}
    </div>
  );

  return <Card title="Activation link" body={body} />;
};

export default ActivationLinkCard;